import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { useDealStore } from '../../lib/stores/dealStore';
import { contractRenewalService } from '../../lib/services/contractRenewalService';
import { useApp } from '../AppProvider';
import { Deal, DealService } from '../../lib/types';

interface ContractRenewalSectionProps {
  deal: Deal;
  onUpdate?: () => void;
}

interface RenewalInfo {
  dealServiceId: string;
  serviceName: string;
  expiryDate: string;
  renewalStatus: 'active' | 'expired' | 'renewed' | 'cancelled';
  daysUntilExpiry: number;
  hasManualRenewal: boolean;
}

export function ContractRenewalSection({ deal, onUpdate }: ContractRenewalSectionProps) {
  const [renewals, setRenewals] = useState<RenewalInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const { updateContractRenewal, duplicateDeal } = useDealStore();
  const { toast } = useApp();
  const navigate = useNavigate();

  useEffect(() => {
    loadRenewalInfo();
  }, [deal.id, deal.status, deal.deal_services]);

  const loadRenewalInfo = async () => {
    // Solo per deal accepted
    if (!deal.deal_services || deal.status !== 'accepted') {
      console.log(`[RENEWAL SECTION] Deal ${deal.id} not accepted or no services, status: ${deal.status}`);
      return;
    }

    console.log(`[RENEWAL SECTION] Loading renewal info for deal ${deal.id} with ${deal.deal_services.length} services`);
    const renewalData: RenewalInfo[] = [];

    // Servizi con rinnovo personalizzato (renewal_task_intervals): la scadenza corretta è
    // quella della task di rinnovo aperta in sales_activities — la stessa fonte già usata
    // da Dashboard e Cronologia Note, sempre ricalcolata quando cambia signed_at. Non usiamo
    // deal_services.contract_expiry_date perché non viene mai ricalcolato per questi servizi.
    const customRenewalDueDates = await contractRenewalService.getCustomRenewalDueDates(deal.id);

    for (const service of deal.deal_services) {
      const hasCustomRenewal = !!(service.service?.has_renewal_tasks && service.service?.renewal_task_intervals?.length);
      // Mostra tutti i servizi che hanno dati di rinnovo o sono ricorrenti
      const hasRenewalData = service.contract_expiry_date || service.requires_manual_renewal;
      const isRecurring = service.service?.service_type === 'monthly' || service.service?.service_type === 'annual';

      console.log(`[RENEWAL SECTION] Service ${service.id}: hasRenewalData=${hasRenewalData}, isRecurring=${isRecurring}, hasCustomRenewal=${hasCustomRenewal}, type=${service.service?.service_type}`);

      if (hasCustomRenewal || hasRenewalData || isRecurring) {
        const today = new Date();
        let expiryDate: Date;

        const customDueDate = hasCustomRenewal ? customRenewalDueDates.get(service.id) : undefined;

        if (customDueDate) {
          expiryDate = new Date(customDueDate.dueDate);
        } else if (service.contract_expiry_date) {
          // Usa la data di scadenza del contratto se disponibile, altrimenti calcola
          // partendo dalla data di accettazione (signed_at) — non da oggi, altrimenti
          // la scadenza si sposterebbe ogni volta che si riapre la pagina (R03).
          expiryDate = new Date(service.contract_expiry_date);
        } else {
          // Base = signed_at se valorizzata, fallback = oggi (deal non ancora accettato)
          const baseDate = deal.signed_at ? new Date(deal.signed_at) : new Date(today);
          expiryDate = new Date(baseDate);
          if (service.service?.service_type === 'annual') {
            expiryDate.setFullYear(expiryDate.getFullYear() + 1);
          } else {
            expiryDate.setMonth(expiryDate.getMonth() + 1);
          }
        }

        const daysUntilExpiry = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

        renewalData.push({
          dealServiceId: service.id,
          serviceName: service.service?.title || service.custom_service_name || 'Servizio',
          expiryDate: expiryDate.toISOString().split('T')[0],
          renewalStatus: service.renewal_status || 'active',
          daysUntilExpiry,
          hasManualRenewal: service.requires_manual_renewal || false
        });
      }
    }

    setRenewals(renewalData);
  };

  const handleRenewalAction = async (dealServiceId: string, action: 'renew' | 'cancel') => {
    setLoading(true);
    try {
      // Audit R03: il rinnovo deve partire dalla scadenza precedente (mantiene il
      // pattern annuale del contratto), NON da "oggi" — altrimenti rinnovi tardivi
      // shiftano permanentemente il ciclo.
      const currentRenewal = renewals.find((r) => r.dealServiceId === dealServiceId);
      let newExpiryDate: string | undefined;
      if (action === 'renew') {
        const baseExpiry = currentRenewal?.expiryDate
          ? new Date(currentRenewal.expiryDate)
          : new Date();
        baseExpiry.setFullYear(baseExpiry.getFullYear() + 1);
        newExpiryDate = baseExpiry.toISOString().split('T')[0];
      }
      const renewalData = {
        renewalStatus: action === 'renew' ? 'renewed' : 'cancelled',
        newExpiryDate,
      };

      const result = await updateContractRenewal(dealServiceId, renewalData, deal.id);

      if (result) {
        // Aggiorna lo stato locale solo se l'update ha avuto successo
        setRenewals(prev => prev.map(renewal =>
          renewal.dealServiceId === dealServiceId
            ? { ...renewal, renewalStatus: renewalData.renewalStatus as any }
            : renewal
        ));

        // Se è un rinnovo, crea una nuova offerta (deal) in bozza
        if (action === 'renew') {
          const newDeal = await duplicateDeal(deal.id, `Rinnovo - ${deal.title}`);
          if (newDeal) {
            toast?.success(`Nuova offerta "${newDeal.title}" creata in bozza`);
            // Naviga al nuovo deal dopo un breve delay per permettere il feedback visivo
            setTimeout(() => navigate(`/deals/${newDeal.id}`), 1500);
          }
        }

        onUpdate?.();
      }
    } catch (error) {
      console.error('Error updating renewal:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string, daysUntilExpiry: number) => {
    switch (status) {
      case 'renewed':
        return <CheckCircle className="h-4 w-4 text-[#0F7B6C] dark:text-green-400" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4 text-[#D44C47] dark:text-red-400" />;
      case 'expired':
        return <AlertTriangle className="h-4 w-4 text-[#D44C47] dark:text-red-400" />;
      default:
        return daysUntilExpiry <= 30 ?
          <AlertTriangle className="h-4 w-4 text-[#D9730D] dark:text-orange-400" /> :
          <Clock className="h-4 w-4 text-[#2383E2] dark:text-blue-400" />;
    }
  };

  const getStatusBadge = (status: string, daysUntilExpiry: number) => {
    if (status === 'renewed') return <Badge className="bg-[rgba(15,123,108,0.1)] dark:bg-green-900/30 text-[#0F7B6C] dark:text-green-300 text-[11px]">Rinnovato</Badge>;
    if (status === 'cancelled') return <Badge className="bg-[rgba(212,76,71,0.1)] dark:bg-red-900/30 text-[#D44C47] dark:text-red-300 text-[11px]">Annullato</Badge>;
    if (status === 'expired') return <Badge className="bg-[rgba(212,76,71,0.1)] dark:bg-red-900/30 text-[#D44C47] dark:text-red-300 text-[11px]">Scaduto</Badge>;

    if (daysUntilExpiry <= 0) return <Badge className="bg-[rgba(212,76,71,0.1)] dark:bg-red-900/30 text-[#D44C47] dark:text-red-300 text-[11px]">Scaduto</Badge>;
    if (daysUntilExpiry <= 7) return <Badge className="bg-[rgba(217,115,13,0.1)] dark:bg-orange-900/30 text-[#D9730D] dark:text-orange-300 text-[11px]">In scadenza</Badge>;
    if (daysUntilExpiry <= 30) return <Badge className="bg-[rgba(203,145,47,0.1)] dark:bg-yellow-900/30 text-[#CB912F] dark:text-yellow-300 text-[11px]">Da rinnovare</Badge>;

    return <Badge className="bg-[rgba(35,131,226,0.1)] dark:bg-blue-900/30 text-[#2383E2] dark:text-blue-300 text-[11px]">Attivo</Badge>;
  };

  if (renewals.length === 0) {
    // Messaggio diverso se il deal non è accepted
    if (deal.status !== 'accepted') {
      return (
        <div className="border border-[rgba(55,53,47,0.09)] dark:border-gray-700 rounded-lg bg-white dark:bg-[#2F3437] p-4">
          <div className="text-center py-2">
            <Calendar className="h-6 w-6 mx-auto mb-2 text-[#787774]/50 dark:text-gray-500" />
            <p className="text-sm font-medium mb-1 text-[#37352F] dark:text-white">Rinnovi disponibili solo per contratti accettati</p>
            <p className="text-xs text-[#787774] dark:text-gray-400">
              I rinnovi saranno visibili quando il deal avrà status "Accettato".
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="border border-[rgba(55,53,47,0.09)] dark:border-gray-700 rounded-lg bg-white dark:bg-[#2F3437] p-4">
        <div className="text-center py-2">
          <Calendar className="h-6 w-6 mx-auto mb-2 text-[#787774]/50 dark:text-gray-500" />
          <p className="text-sm font-medium mb-1 text-[#37352F] dark:text-white">Nessun contratto da rinnovare</p>
          <p className="text-xs text-[#787774] dark:text-gray-400">
            I contratti con rinnovo manuale o date di scadenza appariranno qui.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-[rgba(55,53,47,0.09)] dark:border-gray-700 rounded-lg bg-white dark:bg-[#2F3437]">
      <div className="flex items-center gap-2 p-4 pb-3 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700">
        <Calendar className="h-4 w-4 text-[#787774] dark:text-gray-400" />
        <h3 className="text-sm font-medium text-[#37352F] dark:text-white">Rinnovi Contratti</h3>
      </div>

      <div className="p-4 space-y-0">
        {renewals.map((renewal, index) => (
          <div
            key={renewal.dealServiceId}
            className={`py-3 ${index > 0 ? 'border-t border-[rgba(55,53,47,0.09)] dark:border-gray-800' : ''} hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.04] transition-colors`}
          >
            {/* Desktop layout */}
            <div className="hidden md:flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getStatusIcon(renewal.renewalStatus, renewal.daysUntilExpiry)}
                <div>
                  <h4 className="text-sm font-medium text-[#37352F] dark:text-white">{renewal.serviceName}</h4>
                  <p className="text-xs text-[#787774] dark:text-gray-400">
                    Scadenza: {new Date(renewal.expiryDate).toLocaleDateString('it-IT')}
                    {renewal.daysUntilExpiry > 0 && (
                      <span className="ml-2">
                        ({renewal.daysUntilExpiry} giorni)
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {getStatusBadge(renewal.renewalStatus, renewal.daysUntilExpiry)}

                {renewal.renewalStatus === 'active' && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRenewalAction(renewal.dealServiceId, 'renew')}
                      disabled={loading}
                      className="text-xs h-7 px-2.5"
                    >
                      Rinnova
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRenewalAction(renewal.dealServiceId, 'cancel')}
                      disabled={loading}
                      className="text-xs h-7 px-2.5"
                    >
                      Annulla
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile layout */}
            <div className="md:hidden space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 flex-1 min-w-0">
                  {getStatusIcon(renewal.renewalStatus, renewal.daysUntilExpiry)}
                  <div className="min-w-0">
                    <h4 className="text-sm font-medium text-[#37352F] dark:text-white truncate">{renewal.serviceName}</h4>
                    <p className="text-xs text-[#787774] dark:text-gray-400 mt-0.5">
                      Scadenza: {new Date(renewal.expiryDate).toLocaleDateString('it-IT')}
                    </p>
                    {renewal.daysUntilExpiry > 0 && (
                      <p className="text-[10px] text-[#787774] dark:text-gray-500">
                        {renewal.daysUntilExpiry} giorni rimanenti
                      </p>
                    )}
                  </div>
                </div>
                {getStatusBadge(renewal.renewalStatus, renewal.daysUntilExpiry)}
              </div>

              {renewal.renewalStatus === 'active' && (
                <div className="flex gap-2 pt-1 pl-7">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRenewalAction(renewal.dealServiceId, 'renew')}
                    disabled={loading}
                    className="flex-1 text-xs h-8"
                  >
                    Rinnova
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRenewalAction(renewal.dealServiceId, 'cancel')}
                    disabled={loading}
                    className="flex-1 text-xs h-8"
                  >
                    Annulla
                  </Button>
                </div>
              )}
            </div>

            {renewal.daysUntilExpiry <= 30 && renewal.renewalStatus === 'active' && (
              <div className="mt-2 ml-7 md:ml-8 p-2 bg-[rgba(255,150,50,0.08)] dark:bg-orange-900/20 rounded text-xs text-[#D9730D] dark:text-orange-300">
                <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />
                <span className="hidden md:inline">Rinnovo richiesto entro {renewal.daysUntilExpiry} giorni. Contattare il cliente.</span>
                <span className="md:hidden">Rinnovo entro {renewal.daysUntilExpiry} giorni</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}